import {
  BlockingActionOverlay,
  Button,
  Group,
  IconButton,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Stack,
  Text,
  Tooltip,
} from "@assetwatch/ui";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useMotionAmplificationJobDownload } from "../../queries/useMotionAmplificationJobDownload";
import { MotionAmplificationRawVideoModal } from "../motion-amplification/MotionAmplificationRawVideoModal";
import type { JobView } from "../motion-amplification/types";
import {
  actionLabel,
  getJobProgress,
  statusLabel,
} from "./motionAmplificationJobState";

function VideoDownloadMenu({ view, t }: { view: JobView; t: TFunction }) {
  const { job, uploadState } = view;
  const [rawVideoUrl, setRawVideoUrl] = useState<string | null>(null);
  const { download, error, getRawVideoUrl, isDownloading } =
    useMotionAmplificationJobDownload();
  const isProcessedVideoAvailable =
    job.status === "Completed" && Boolean(job.processedObjectKey);
  const isUploading = uploadState === "uploading";
  const isRawVideoAvailable = Boolean(job.rawObjectKey) && !isUploading;

  async function handleViewRawVideo() {
    try {
      setRawVideoUrl(await getRawVideoUrl(job.jobId));
    } catch {
      // The hook exposes the failure as an inline error below the menu.
      return;
    }
  }

  return (
    <Stack gap={4}>
      <Group justify="between">
        <Tooltip label={t("motionAmplification.downloadProcessedVideo")}>
          <IconButton
            aria-label={t("motionAmplification.downloadProcessedVideo")}
            disabled={
              !isProcessedVideoAvailable || isDownloading || isUploading
            }
            icon="download"
            onClick={() => void download(job.jobId, "processed")}
            variant="ghost"
          />
        </Tooltip>
        <Menu modal={false}>
          <MenuTrigger
            render={
              <Button
                icon="chevron-down"
                disabled={isDownloading || isUploading}
                size="sm"
                variant="outline"
              >
                {t("motionAmplification.rawActions")}
              </Button>
            }
          />
          <MenuPopup align="end" size="md">
            <MenuItem
              disabled={!isRawVideoAvailable}
              onClick={() => void handleViewRawVideo()}
            >
              {t("motionAmplification.viewRawVideo")}
            </MenuItem>
            <MenuItem
              disabled={!isRawVideoAvailable}
              onClick={() => void download(job.jobId, "original")}
            >
              {t("motionAmplification.downloadOriginalVideo")}
            </MenuItem>
          </MenuPopup>
        </Menu>
      </Group>
      {error && (
        <Text tone="negative">
          {t("motionAmplification.downloadVideoError")}
        </Text>
      )}
      <MotionAmplificationRawVideoModal
        open={rawVideoUrl !== null}
        title={job.name || job.fileName || t("motionAmplification.selectVideo")}
        videoUrl={rawVideoUrl}
        onOpenChange={(open) => {
          if (!open) setRawVideoUrl(null);
        }}
      />
    </Stack>
  );
}

export function JobMedia({ view, t }: { view: JobView; t: TFunction }) {
  const { job, uploadState } = view;
  if (job.processedVideoUrl && job.status === "Completed") {
    return (
      <Stack gap={8}>
        <video
          key={job.processedVideoUrl}
          controls
          preload="metadata"
          className="aspect-video w-full"
          src={job.processedVideoUrl}
        >
          <track kind="captions" />
        </video>
        <VideoDownloadMenu view={view} t={t} />
      </Stack>
    );
  }

  const isUploading = uploadState === "uploading";
  const isProcessing = job.status === "Processing";
  const isStarting = uploadState === "starting";

  return (
    <Stack gap={8}>
      <BlockingActionOverlay
        label={actionLabel(
          isUploading
            ? t("motionAmplification.uploadingVideo")
            : t("motionAmplification.processing"),
          isStarting ? undefined : getJobProgress(view),
        )}
        open={isUploading || isProcessing || isStarting}
      >
        <div className="flex aspect-video items-center justify-center bg-surface-container">
          {!isUploading && !isProcessing && !isStarting && (
            <Text>{statusLabel(job, t, uploadState)}</Text>
          )}
        </div>
      </BlockingActionOverlay>
      <VideoDownloadMenu view={view} t={t} />
    </Stack>
  );
}
